/* AUTO-SYNCED from PsyKit.io/components/bilateral/MirrorCanvas.tsx — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

import { useRef, useEffect } from "react";

interface MirrorCanvasProps {
  /** Normalised position snapshot from the therapist (0–1). Used only as initial seed. */
  xNorm: number;
  /** Current tempo in Hz — same value used in BallCanvas. */
  hz: number;
  /** Elapsed seconds since session start on the therapist's side. */
  elapsedSeconds: number;
  ballSize: number;
  ballColor: string;
  canvasBg: string;
  isRunning: boolean;
  /** Called every frame with the interpolated xNorm value (0–1) for audio panning. */
  onPositionChange?: (xNorm: number) => void;
  /** Fired once per half-cycle (each edge crossing), with the side just reached.
   *  Edge detection runs against the local-clock-extrapolated sinusoid, so it
   *  stays accurate even between sparse therapist sync frames. */
  onPassComplete?: (side: "left" | "right") => void;
}

function lighten(hex: string, amt: number): string {
  const r = Math.min(255, parseInt(hex.slice(1, 3), 16) + amt);
  const g = Math.min(255, parseInt(hex.slice(3, 5), 16) + amt);
  const b = Math.min(255, parseInt(hex.slice(5, 7), 16) + amt);
  return `rgb(${r},${g},${b})`;
}

function darken(hex: string, amt: number): string {
  const r = Math.max(0, parseInt(hex.slice(1, 3), 16) - amt);
  const g = Math.max(0, parseInt(hex.slice(3, 5), 16) - amt);
  const b = Math.max(0, parseInt(hex.slice(5, 7), 16) - amt);
  return `rgb(${r},${g},${b})`;
}

function isLightColor(hex: string): boolean {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return r * 0.299 + g * 0.587 + b * 0.114 > 140;
}

/**
 * Full-screen mirror canvas for the client/patient view.
 *
 * Sync strategy: the therapist broadcasts (hz, elapsedSeconds) ~20fps via Supabase.
 * The client runs the same sinusoidal formula locally at 60fps, extrapolating
 * forward from the last received snapshot using its own clock:
 *
 *   localElapsed = receivedElapsed + (performance.now() − receivedAt) / 1000
 *   x = centerX + amplitude × sin(2π × hz × localElapsed)
 *
 * This gives frame-rate-independent, window-size-independent motion that stays
 * within ~50ms of the therapist (one network round-trip) — clinically imperceptible.
 */
export default function MirrorCanvas({
  xNorm,
  hz,
  elapsedSeconds,
  ballSize,
  ballColor,
  canvasBg,
  isRunning,
  onPositionChange,
  onPassComplete,
}: MirrorCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);
  const loopRef = useRef<(() => void) | null>(null); // stable ref so restart effect can kick the loop
  // Stable refs so the rAF loop always reads the latest callbacks without re-mounting
  const onPositionChangeRef = useRef(onPositionChange);
  useEffect(() => { onPositionChangeRef.current = onPositionChange; }, [onPositionChange]);
  const onPassCompleteRef = useRef(onPassComplete);
  useEffect(() => { onPassCompleteRef.current = onPassComplete; }, [onPassComplete]);

  // Tracks the previous frame's localElapsed for cosine-sign edge detection.
  // Reset to 0 each time isRunning transitions false → true so we don't fire
  // a spurious pass on the first post-pause frame.
  const prevLocalElapsedRef = useRef(0);

  // All animation state lives here — no React state, no re-renders from the loop
  const stateRef = useRef({
    receivedElapsed: elapsedSeconds, // therapist's elapsed at last frame
    receivedAt: performance.now(),   // local clock when last frame arrived
    hz,
    isRunning,
    ballSize,
    ballColor,
    canvasBg,
    frozenX: xNorm,                  // normalised X when stopped
  });

  // Snap to therapist state whenever props update (arrives ~20fps from Supabase)
  useEffect(() => {
    const s = stateRef.current;
    s.receivedElapsed = elapsedSeconds;
    s.receivedAt = performance.now(); // mark when this correction arrived
    s.hz = hz;
    s.isRunning = isRunning;
    s.ballSize = ballSize;
    s.ballColor = ballColor;
    s.canvasBg = canvasBg;
    s.frozenX = xNorm; // used when stopped

    // When the ball is parked (not running) the RAF loop isn't drawing, so a
    // visual change — color, size, background — wouldn't appear until the ball
    // next moves. Kick a SINGLE repaint so the client sees it immediately. The
    // loop self-parks again at the end (it only reschedules while isRunning),
    // and the !rafRef guard means we never stack frames.
    if (!isRunning && !rafRef.current && loopRef.current) {
      rafRef.current = requestAnimationFrame(loopRef.current);
    }
  }, [xNorm, hz, elapsedSeconds, isRunning, ballSize, ballColor, canvasBg]);

  // RAF loop — started on mount for initial draw, then parked until isRunning=true
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const loop = () => {
      const ctx = canvas.getContext("2d");
      if (!ctx) { rafRef.current = requestAnimationFrame(loop); return; }

      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;

      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        ctx.scale(dpr, dpr);
      }

      const s = stateRef.current;
      const radius = s.ballSize;
      const color = s.ballColor;
      const bg = s.canvasBg;

      let ballX: number;

      if (s.isRunning && w > 0) {
        // Extrapolate therapist's elapsed time using local clock
        const localElapsed =
          s.receivedElapsed + (performance.now() - s.receivedAt) / 1000;

        // Same formula as BallCanvas: x = centerX + amplitude × sin(2π × Hz × t)
        // Reach within 12px of each edge, accounting for ball radius
        const amplitude = w / 2 - s.ballSize - 12;
        ballX = w / 2 + amplitude * Math.sin(2 * Math.PI * s.hz * localElapsed);

        // Edge detection — mirrors BallCanvas:188-201. cos changes sign at
        // every peak of sin, so a sign flip from one frame to the next means
        // we just crossed an edge. sin's sign at the current frame tells us
        // which side (sin > 0 ⇒ right grip).
        const prevLocal = prevLocalElapsedRef.current;
        if (prevLocal > 0) {
          const prevCos = Math.cos(2 * Math.PI * s.hz * prevLocal);
          const currCos = Math.cos(2 * Math.PI * s.hz * localElapsed);
          if (prevCos * currCos < 0) {
            const side: "left" | "right" =
              Math.sin(2 * Math.PI * s.hz * localElapsed) > 0 ? "right" : "left";
            onPassCompleteRef.current?.(side);
          }
        }
        prevLocalElapsedRef.current = localElapsed;
      } else {
        // Stopped — hold at last authoritative position
        ballX = s.frozenX * w;
        // Reset so resuming doesn't fire a spurious pass from a stale prev value.
        prevLocalElapsedRef.current = 0;
      }

      const ballY = h / 2;

      // Notify caller of current normalised position (for audio panning)
      if (w > 0) onPositionChangeRef.current?.(ballX / w);

      // ── Draw ──
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // Vignette
      const light = isLightColor(bg);
      const vig = ctx.createRadialGradient(w / 2, h / 2, h * 0.3, w / 2, h / 2, h * 0.85);
      vig.addColorStop(0, "rgba(0,0,0,0)");
      vig.addColorStop(1, light ? "rgba(0,0,0,0.1)" : "rgba(0,0,0,0.45)");
      ctx.fillStyle = vig;
      ctx.fillRect(0, 0, w, h);

      // Glow ring
      const glow = ctx.createRadialGradient(ballX, ballY, radius * 0.5, ballX, ballY, radius * 1.8);
      glow.addColorStop(0, color + "22");
      glow.addColorStop(1, "transparent");
      ctx.beginPath();
      ctx.arc(ballX, ballY, radius * 1.8, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();

      // Ball with 3D radial gradient
      const grad = ctx.createRadialGradient(
        ballX - radius * 0.3, ballY - radius * 0.3, radius * 0.1,
        ballX, ballY, radius
      );
      grad.addColorStop(0, lighten(color, 30));
      grad.addColorStop(0.6, color);
      grad.addColorStop(1, darken(color, 25));
      ctx.beginPath();
      ctx.arc(ballX, ballY, radius, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();

      // Park the loop when stopped; only reschedule while running
      if (stateRef.current.isRunning) {
        rafRef.current = requestAnimationFrame(loop);
      } else {
        rafRef.current = null;
      }
    };

    loopRef.current = loop;
    // One initial RAF to draw the ball at its parked position on mount
    rafRef.current = requestAnimationFrame(loop);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []); // mount only — loop reads stateRef

  // Restart the loop when isRunning transitions false → true
  useEffect(() => {
    if (isRunning && !rafRef.current && loopRef.current) {
      rafRef.current = requestAnimationFrame(loopRef.current);
    }
  }, [isRunning]);

  return <canvas ref={canvasRef} className="block w-full h-full" />;
}
